/**
 * AdminFinancialTenantTable - Tabela detalhada de MRR por tenant
 * Exibe cada tenant pagante com plano, addons, MRR individual, status,
 * valor real do Stripe e desconto aplicado
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { TenantDetail } from '@/hooks/useAdminFinancial';

/** Formata valores monetários em BRL */
const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** Badge de status com cores semânticas */
function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { label: string; className: string }> = {
    active: { label: 'Ativo', className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
    trialing: { label: 'Trial', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
    past_due: { label: 'Inadimplente', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  };
  const v = variants[status] || { label: status, className: '' };
  return <Badge variant="outline" className={v.className}>{v.label}</Badge>;
}

/** Retorna classe de cor baseada na comparação MRR vs Stripe */
function getStripeColorClass(mrr: number, stripeAmount: number | null): string {
  if (stripeAmount === null) return 'text-muted-foreground';
  if (stripeAmount === mrr) return 'text-emerald-600';
  if (stripeAmount === 0) return 'text-destructive';
  return 'text-amber-600';
}

interface Props {
  tenantDetails: TenantDetail[];
  /** Indica se dados do Stripe estão carregando */
  isLoadingStripe?: boolean;
}

export function AdminFinancialTenantTable({ tenantDetails, isLoadingStripe = false }: Props) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Detalhamento por Tenant</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead className="text-right">Valor Plano</TableHead>
              <TableHead>Add-ons</TableHead>
              <TableHead className="text-right">Valor Add-ons</TableHead>
              <TableHead className="text-right">MRR</TableHead>
              <TableHead className="text-right">Valor Stripe</TableHead>
              <TableHead className="text-center">Desconto</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Período</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenantDetails.map((t) => (
              <TableRow key={t.tenant_id}>
                <TableCell className="font-medium max-w-[200px] truncate" title={t.tenant_name}>
                  {t.tenant_name}
                </TableCell>
                <TableCell>{t.plan_name || '-'}</TableCell>
                <TableCell className="text-right">{formatBRL(t.plan_value)}</TableCell>
                <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground" title={t.addons.join(', ')}>
                  {t.addons.length > 0 ? t.addons.join(', ') : '-'}
                </TableCell>
                <TableCell className="text-right">{formatBRL(t.addons_value)}</TableCell>
                <TableCell className="text-right font-bold">{formatBRL(t.mrr)}</TableCell>

                {/* Coluna Valor Stripe */}
                <TableCell className={`text-right font-bold ${getStripeColorClass(t.mrr, t.stripe_amount)}`}>
                  {isLoadingStripe ? (
                    <Skeleton className="h-4 w-16 ml-auto" />
                  ) : t.stripe_amount !== null ? (
                    formatBRL(t.stripe_amount)
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </TableCell>

                {/* Coluna Desconto */}
                <TableCell className="text-center">
                  {isLoadingStripe ? (
                    <Skeleton className="h-5 w-12 mx-auto" />
                  ) : t.discount_percent !== null && t.discount_percent > 0 ? (
                    <Badge
                      variant="outline"
                      className="bg-amber-500/10 text-amber-600 border-amber-500/20"
                      title={t.coupon_name || undefined}
                    >
                      {t.discount_percent}%{t.coupon_name ? ` (${t.coupon_name})` : ''}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </TableCell>

                <TableCell className="text-center"><StatusBadge status={t.status} /></TableCell>
                <TableCell className="text-center text-xs text-muted-foreground">
                  {t.billing_period === 'yearly' ? 'Anual' : 'Mensal'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
